import React, { useEffect, useState } from "react";
import {
  AdditionalArea,
  PathForm,
} from "@via/pages/data/data-sources/cloud-data-sources/amazon-s3/amazon-folder-browser.tsx";
import { DataConnectionCredentials, DataSourceProvider } from "common";
import { FileBrowser } from "@via/pages/data/data-sources/cloud-data-sources/common/file-browser/file-browser.tsx";
import { useForm } from "react-hook-form";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import FileFilters from "@via/pages/data/data-sources/cloud-data-sources/common/file-browser/additional-area/file-filters.tsx";
import {
  BucketObject,
  buildFullPath,
  formatBucketObjects,
  getBucketIdentifier,
} from "@via/pages/data/data-sources/utils/helpers.tsx";
import { BookmarkIcon } from "@heroicons/react/24/outline";
import SaveCurrentDataSource from "@via/pages/data/data-sources/cloud-data-sources/common/file-browser/additional-area/save-current-data-source.tsx";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { Tooltip } from "react-tooltip";
import { datasourceOptions } from "@via/utils/form-validators.tsx";

interface FileBrowserWrapperProps {
  dataSourceProvider: DataSourceProvider;
  connectionCredentials: DataConnectionCredentials;
  folderList: any;
  onPathChange: (payload: PathForm) => void;
  onObjectClick: (object: BucketObject) => void;
  getExtraFolderObjects: any;
  onContinue: any;
  isGetFolderListLoading: boolean;
  isGetExtraFolderListLoading: boolean;
  setShowFileUpload?: (showFileUpload: boolean) => void;
  cart?: BucketObject[];
  setCart?: (cart: BucketObject[]) => void;
  onPathValueChange?: (pathValue: string) => void;
}

export const FileBrowserWrapper: React.FC<FileBrowserWrapperProps> = ({
  dataSourceProvider,
  connectionCredentials,
  folderList,
  onPathChange,
  onObjectClick,
  getExtraFolderObjects,
  onContinue,
  isGetFolderListLoading,
  isGetExtraFolderListLoading,
  setShowFileUpload,
  cart,
  setCart,
  onPathValueChange,
}) => {
  const [isBookmarkModalOpen, setIsBookmarkModalOpen] = useState(false);

  const [additionalArea, setAdditionalArea] = useState<AdditionalArea | null>(
    null
  );
  const [filter, setFilter] = useState("");
  const [bucketObjectList, setBucketObjectList] = useState<BucketObject[]>([]);

  useEffect(() => {
    setBucketObjectList(formatBucketObjects(folderList, dataSourceProvider));
  }, [folderList]);

  const { control, handleSubmit, setValue, watch } = useForm<PathForm>({
    mode: "onChange",
    defaultValues: {
      path: connectionCredentials?.bucketName,
    },
  });
  const pathValue = watch("path");

  useEffect(() => {
    setValue("path", buildFullPath(connectionCredentials, dataSourceProvider));
  }, [connectionCredentials]);

  useEffect(() => {
    if (onPathValueChange && pathValue) {
      onPathValueChange(pathValue);
    }
  }, [pathValue, onPathValueChange]);

  const handlePathSubmit = (payload: PathForm) => {
    onPathChange(payload);
  };

  const handleObjectClick = (object: BucketObject) => {
    onObjectClick(object);
  };

  const handleAdditionalAreaButtonClick = (value: AdditionalArea | null) => {
    if (value === additionalArea) {
      setAdditionalArea(null);
    } else {
      setAdditionalArea(value);
    }
  };

  useEffect(() => {
    if (additionalArea !== AdditionalArea.Filters) {
      setFilter("");
    }
  }, [additionalArea]);

  return (
    <div>
      <div>
        <div className="flex items-start gap-2 mt-2">
          <form
            id="pathForm"
            className="flex-1"
            onSubmit={e => {
              e.preventDefault();
              void handleSubmit(handlePathSubmit)(e);
            }}>
            <FormInputComponent
              name="path"
              placeholder={getBucketIdentifier(dataSourceProvider)}
              showRequiredField={true}
              control={control}
              validations={datasourceOptions.path}
            />
          </form>
          <button
            id="file-browser-search-button"
            className="mt-2 inline-flex justify-center rounded-md bg-indigo-600 p-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            form="pathForm"
            type="submit">
            <MagnifyingGlassIcon className="h-5 w-5" />
          </button>
          <Tooltip
            anchorSelect="#file-browser-search-button"
            content="Search files in given path."
          />
          <button
            id="file-browser-bookmark-button"
            className="mt-2 inline-flex justify-center rounded-md bg-indigo-600 p-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            onClick={() => setIsBookmarkModalOpen(true)}>
            <BookmarkIcon className="h-5 w-5" />
          </button>
          <Tooltip
            anchorSelect="#file-browser-bookmark-button"
            content="Bookmark this location details as a datasource for quick access."
          />
        </div>
        {isBookmarkModalOpen && (
          <SaveCurrentDataSource
            dataSourceProvider={dataSourceProvider}
            credentialId={connectionCredentials?.savedCredentialId}
            path={pathValue}
            open={isBookmarkModalOpen}
            setOpen={setIsBookmarkModalOpen}
          />
        )}

        {additionalArea ? (
          <div className="mt-4 rounded-lg border bg-blue-50/50 px-3 py-4">
            {additionalArea === AdditionalArea.Filters ? (
              <FileFilters filterValue={filter} setFilterValue={setFilter} />
            ) : null}
          </div>
        ) : null}

        <FileBrowser
          dataSourceProvider={dataSourceProvider}
          objects={bucketObjectList}
          onObjectClick={handleObjectClick}
          getExtraFolderObjects={getExtraFolderObjects}
          onContinue={onContinue}
          isGetFolderListLoading={isGetFolderListLoading}
          isGetExtraFolderListLoading={isGetExtraFolderListLoading}
          filterValue={filter}
          onAdditionalAreaButtonClick={handleAdditionalAreaButtonClick}
          additionalArea={additionalArea}
          setAdditionalArea={setAdditionalArea}
          setShowFileUpload={setShowFileUpload}
          cart={cart}
          setCart={setCart}
        />
      </div>
    </div>
  );
};

export default FileBrowserWrapper;
